import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { CourseEntity } from 'src/entities/Course.entity';
import { TimetableDetailEntity, TimetablePayload } from 'src/entities/Timetable.entity';
import { isTimetableStale, releaseTimetableSave, reloadTimetableDetail, tryAcquireTimetableSave } from 'src/hooks/timetableSaveGate';
import { invalidateCourseMaster } from 'src/hooks/useCourseMutations';
import { useTimetableQuery } from 'src/hooks/useTimetable.query';
import { useDeleteTimetableMutation, useUpdateTimetableMutation } from 'src/hooks/useTimetableMutations';
import { CourseMasterError, parseCourseMasterError } from 'src/utils/courseMasterError';

import AddCoursesDialog, { TimetableRow } from './AddCoursesDialog';
import { courseCodeLabel, courseLabel, fmtStamp, multiTripCodes } from './courseMasterUtils';
import RouteSourceDialog from './RouteSourceDialog';
import { useIsTimetableSaving } from './useIsTimetableSaving';
import { useIsTimetableSaveLocked, useIsTimetableStale } from './useIsTimetableStale';

const muted = 'text.secondary';
// サンプル（route-planner d9aa3bb）の列幅。見出しと行で同じ列にそろえる
const COURSE_COLS = '40px 96px minmax(0,1fr) 88px';
const ROW_GRID = `24px ${COURSE_COLS} 40px`;
type Props = {
  timetableId: number; courses: CourseEntity[];
  /** コース一覧を取得済みか。取得前は便区分の判定をダイヤ詳細の所属コースで代える */
  coursesLoaded: boolean;
  onCourse: (courseId: number) => void; onDeleted: () => void };

export default function TimetableDetail({ timetableId, courses, coursesLoaded, onCourse, onDeleted }: Props) {
  const detail = useTimetableQuery(timetableId);
  if (detail.isPending) return <Box sx={{ p: 4, color: muted }}>読み込み中…</Box>;
  if (!detail.data) return <Box sx={{ p: 4, color: muted }}>ダイヤを読み込めませんでした</Box>;
  return <TimetableEditor key={detail.data.id} timetable={detail.data} courses={courses} coursesLoaded={coursesLoaded} onCourse={onCourse} onDeleted={onDeleted} />;
}

type RoutePrompt = Extract<CourseMasterError, { code: 'route_source_required' }>;
/** 送り直し（コピー元の選択・「1便を一緒に入れる」）に使う、失敗した所属と、送ったときのサーバの所属 */
type Attempt = { rows: TimetableRow[]; base: string; message: string };
const rowsOf = (timetable: TimetableDetailEntity): TimetableRow[] => timetable.courseRoutes.map((r) => ({ courseId: r.course.id }));
// updatedAt は秒までしか無く、コースの削除ではダイヤの updatedAt が変わらないので、所属の並びもつないで比べる
const baseOf = (timetable: TimetableDetailEntity) => `${timetable.updatedAt}|${timetable.courseRoutes.map((r) => r.course.id).join(',')}`;
const toPayload = (rows: TimetableRow[]): TimetablePayload => ({
  course_routes: rows.map((r) => ({ course_id: r.courseId, ...(r.copyFromTimetableId ? { copy_from_timetable_id: r.copyFromTimetableId } : {}) })),
});

/**
 * 名前と所属コース（並び・追加・外す）を操作ごとに PATCH する（log_web_issues#401 の 11）。表示する所属は常にサーバの値で、
 * 並べ替えの保存中だけ送った順を出す。同じダイヤの保存中・失敗後の取り直しが終わるまでは次の操作を送らない（全件の所属を送るので、
 * 古い所属から組み立てると他の保存を巻き戻す）。普通の同時保存は API に版の照合が無いので後勝ち
 */
function TimetableEditor({ timetable, courses, coursesLoaded, onCourse, onDeleted }: { timetable: TimetableDetailEntity; courses: CourseEntity[]; coursesLoaded: boolean; onCourse: (courseId: number) => void; onDeleted: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const update = useUpdateTimetableMutation();
  const destroy = useDeleteTimetableMutation();
  const saving = useIsTimetableSaving(timetable.id);
  const stale = useIsTimetableStale(timetable.id);
  // 失敗後の取り直し中もゲートは閉じているので、押せる見た目のまま断られないよう、ゲートを見て操作を止める
  const locked = useIsTimetableSaveLocked(timetable.id);
  const [reloading, setReloading] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<number[] | null>(null);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeId, setRemoveId] = useState<number | null>(null);
  const [sourcePrompt, setSourcePrompt] = useState<RoutePrompt | null>(null);
  const [alert, setAlert] = useState<{ message: string; firstTripIds?: number[] } | null>(null);
  const byId = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);
  // Why not 一覧と詳細を常に合わせる: 削除した 2 便が取り直し中の詳細に残り、消えた「1便」が戻る
  const multi = useMemo(
    () => multiTripCodes(coursesLoaded ? courses : timetable.courseRoutes.map((r) => r.course)),
    [coursesLoaded, courses, timetable],
  );
  // コース一覧の取得前・取り直し中でも、サーバの所属にあるコースは表示できるようにダイヤ詳細の course で補う
  const serverCourses = useMemo(() => new Map(timetable.courseRoutes.map((r) => [r.course.id, r.course])), [timetable]);
  const courseOf = (courseId: number): RowCourse | undefined => byId.get(courseId) ?? serverCourses.get(courseId);
  const placeNamesOf = useMemo(() => new Map(timetable.courseRoutes.map((r) => [r.course.id, r.placeNames])), [timetable]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const serverRows = rowsOf(timetable);
  const shownIds = pendingOrder ?? serverRows.map((r) => r.courseId);
  const busy = saving || stale || locked;
  const base = baseOf(timetable);

  // 送り直しを待つ間に他の利用者がこのダイヤを変えたら、古い所属から組み立てた送り直しで上書きしないよう捨てる
  useEffect(() => {
    if (!attempt || attempt.base === base) return;
    setAttempt(null);
    setSourcePrompt(null);
    setAlert(null);
    enqueueSnackbar('他の利用者がこのダイヤを変更しました。もう一度操作してください', { variant: 'warning' });
  }, [attempt, base, enqueueSnackbar]);

  const warnSaving = () => enqueueSnackbar('保存中です。終わってからもう一度変更してください', { variant: 'warning' });
  const onError = (e: unknown, rows: TimetableRow[] | null, message: string) => {
    const err = parseCourseMasterError(e);
    switch (err.code) {
      case 'route_source_required':
        if (rows) { setAttempt({ rows, base, message }); setSourcePrompt(err); }
        break;
      case 'first_trip_missing': {
        // 今の所属にある 1 便は外そうとした 1 便。足し直すと外す操作が並びの変更に化けるので、足りないのが所属に無い 1 便のときだけ出す
        const missing = [...new Set(err.trips.map((t) => t.firstTripCourse.id))].filter((id) => !serverRows.some((r) => r.courseId === id));
        if (rows && missing.length) setAttempt({ rows, base, message });
        setAlert({ message: `${err.message}（${err.trips.map((t) => `${courseLabel(t)} → ${t.firstTripCourse.code}`).join('、')}）`, firstTripIds: missing.length ? missing : undefined });
        break;
      }
      case 'truck_shared_by_trip_groups': setAlert({ message: `${err.message}（${err.courses.map(courseLabel).join('、')}）` }); break;
      default: enqueueSnackbar(err.message, { variant: 'error' });
    }
  };
  /**
   * 送る直前にゲートを取り、取れなければ（同じダイヤの保存中・失敗後の取り直し中）送らずに false を返す。
   * 失敗したら一覧と詳細を取り直し終えてからゲートを空ける（応答だけ失われた保存の後に、古い所属から次の全件を送らないため）。
   * Why not mutate のコールバック: observer がアンマウントされると呼ばれず、ゲートが空かない。Promise の後続はアンマウント後も走る
   */
  const send = (data: TimetablePayload, message: string, rows: TimetableRow[] | null, onFail?: () => void) => {
    const id = timetable.id;
    if (!tryAcquireTimetableSave(id)) {
      warnSaving();
      return false;
    }
    if (isTimetableStale(id)) {
      releaseTimetableSave(id);
      return false;
    }
    // 送り直しの元（attempt）も捨てる。残すと、この保存で進んだ updatedAt を「他の利用者の変更」と取り違える
    setAlert(null);
    setAttempt(null);
    void update.mutateAsync({ id, data })
      .then(() => { enqueueSnackbar(message, { variant: 'success' }); })
      .catch(async (e: unknown) => {
        setPendingOrder(null);
        onFail?.();
        onError(e, rows, message);
        void invalidateCourseMaster(queryClient);
        await reloadTimetableDetail(queryClient, id);
      })
      .finally(() => {
        setPendingOrder(null);
        releaseTimetableSave(id);
      });
    return true;
  };
  const sendRows = (rows: TimetableRow[], message: string) => send(toPayload(rows), message, rows);

  const saveName = (input: HTMLInputElement) => {
    const v = input.value.trim();
    const server = timetable.name;
    if (!v || v === server) {
      input.value = server;
      return;
    }
    // 送らなかった・失敗した名前を欄に残すと保存済みに見える。key は名前が変わらないと作り直されないので値を戻す
    if (!send({ name: v }, '保存しました', null, () => { input.value = server; })) input.value = server;
  };
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (busy || !over || active.id === over.id) return;
    const from = shownIds.indexOf(Number(active.id));
    const to = shownIds.indexOf(Number(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(shownIds, from, to);
    setPendingOrder(next);
    if (!sendRows(next.map((courseId) => ({ courseId })), '並び順を保存しました')) setPendingOrder(null);
  };
  // 送れなかったとき（取り直し中など）は attempt とダイアログを残し、選んだ内容を捨てない。送れば send が attempt を捨てる
  const addFirstTrips = () => {
    if (!attempt) return;
    const missing = (alert?.firstTripIds ?? []).filter((id) => !attempt.rows.some((r) => r.courseId === id));
    sendRows([...attempt.rows, ...missing.map((courseId) => ({ courseId }))], attempt.message);
  };
  const chooseSource = (timetableId: number) => {
    if (!attempt || !sourcePrompt) return;
    const next = attempt.rows.map((r) => (r.courseId === sourcePrompt.courseId ? { ...r, copyFromTimetableId: timetableId } : r));
    if (sendRows(next, attempt.message)) setSourcePrompt(null);
  };
  const reload = () => {
    setReloading(true);
    void reloadTimetableDetail(queryClient, timetable.id).finally(() => setReloading(false));
  };
  const removeCourse = removeId != null ? courseOf(removeId) : undefined;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
        <Typography sx={{ fontSize: 14, color: muted, whiteSpace: 'nowrap' }}>ダイヤ名：</Typography>
        <Tooltip title="クリックして編集できます">
          <Box sx={{ display: 'flex', alignItems: 'center', height: 42, border: 1, borderColor: 'divider', borderRadius: 1, px: 0.5, '&:hover': { borderColor: 'text.secondary' } }}>
            <InputBase
              key={`name-${timetable.id}-${timetable.name}`}
              defaultValue={timetable.name}
              inputProps={{ 'aria-label': 'ダイヤ名' }}
              disabled={busy}
              sx={{ '&&': { fontSize: 24, fontWeight: 700 }, width: 220 }}
              onBlur={(e) => saveName(e.target as HTMLInputElement)}
            />
          </Box>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', gap: 4, mb: 3, fontSize: 13, color: muted }}>
        <span>作成日時：{fmtStamp(timetable.createdAt)}</span><span>更新日時：{fmtStamp(timetable.updatedAt)}</span>
      </Box>
      {stale && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" disabled={reloading} onClick={reload}>読み込み直す</Button>}
        >
          ダイヤを読み込み直せませんでした
        </Alert>
      )}
      {alert && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => { setAlert(null); setAttempt(null); }}
          action={alert.firstTripIds && attempt ? <Button color="inherit" size="small" disabled={busy} onClick={addFirstTrips}>1便を一緒に入れる</Button> : undefined}
        >
          {alert.message}
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', pb: 1 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700 }}>所属コース <Box component="span" sx={{ color: muted, ml: 1, fontWeight: 400 }}>{shownIds.length}</Box></Typography>
        <Button startIcon={<AddRoundedIcon />} disabled={busy} onClick={() => setAddOpen(true)}>コースを追加</Button>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: ROW_GRID, gap: 1.5, alignItems: 'center', py: 0.75, borderBottom: 1, borderColor: 'divider', fontSize: 12, color: muted, fontWeight: 600 }}>
        <span /><Box component="span" sx={{ textAlign: 'center' }}>順</Box><Box component="span" sx={{ whiteSpace: 'nowrap' }}>コース番号</Box><span>コース名</span><Box component="span" sx={{ whiteSpace: 'nowrap' }}>配送件数</Box><span />
      </Box>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={shownIds} strategy={verticalListSortingStrategy}>
          {shownIds.map((courseId, index) => {
            const course = courseOf(courseId);
            if (!course) return null;
            return (
              <TimetableCourseRow
                key={courseId}
                course={course}
                label={courseCodeLabel(course, multi)}
                placeNames={placeNamesOf.get(course.id) ?? []}
                index={index}
                disabled={busy}
                onCourse={() => onCourse(course.id)}
                onRemove={() => setRemoveId(course.id)}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      {shownIds.length === 0 && <Typography sx={{ py: 3, color: muted }}>所属コースはありません</Typography>}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} disabled={busy} onClick={() => setDeleteOpen(true)}>ダイヤを削除</Button>
      </Box>

      <AddCoursesDialog
        open={addOpen}
        courses={courses}
        currentIds={serverRows.map((r) => r.courseId)}
        onClose={() => setAddOpen(false)}
        onAdd={(added) => { setAddOpen(false); sendRows([...serverRows, ...added], 'コースを追加しました'); }}
      />
      <RouteSourceDialog prompt={sourcePrompt} courses={courses} onChoose={chooseSource} onClose={() => { setSourcePrompt(null); setAttempt(null); }} />
      <Dialog open={removeId != null} onClose={() => setRemoveId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>ダイヤから外しますか？</DialogTitle>
        <DialogContent sx={{ fontSize: 14 }}>
          「{removeCourse ? [courseCodeLabel(removeCourse, multi), removeCourse.name].filter(Boolean).join(' ') : ''}」を「{timetable.name}」から外します。コース自体は削除されません。
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveId(null)}>キャンセル</Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={() => {
              const id = removeId;
              setRemoveId(null);
              if (id != null) sendRows(serverRows.filter((r) => r.courseId !== id), 'ダイヤから外しました');
            }}
          >
            外す
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>ダイヤを削除しますか？</DialogTitle>
        <DialogContent sx={{ fontSize: 14 }}>「{timetable.name}」を削除します。所属コースは削除されず、他のダイヤへの所属も残ります。最後の所属だったコースの並びは未所属に戻ります。</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>キャンセル</Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            disabled={destroy.isPending}
            onClick={() => destroy.mutate({ id: timetable.id }, {
              onSuccess: () => { setDeleteOpen(false); onDeleted(); enqueueSnackbar('ダイヤを削除しました', { variant: 'success' }); },
              onError: (e) => enqueueSnackbar(parseCourseMasterError(e, '削除できませんでした').message, { variant: 'error' }),
            })}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

type RowCourse = Pick<CourseEntity, 'id' | 'code' | 'name' | 'tripNo'>;

function TimetableCourseRow({ course, label, placeNames, index, disabled, onCourse, onRemove }: { course: RowCourse; label: string; placeNames: (string | null)[]; index: number; disabled: boolean; onCourse: () => void; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: course.id, disabled });
  const names = placeNames.map((n) => n ?? '（他社の地点）');
  return (
    <Box
      ref={setNodeRef}
      data-testid="timetable-course"
      sx={{ transform: CSS.Transform.toString(transform), transition, position: 'relative', zIndex: isDragging ? 2 : 'auto',
        bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: 1, borderColor: 'divider', minHeight: 56 }}
    >
      <Tooltip title="ドラッグで並び替え">
        <span><IconButton {...attributes} {...listeners} aria-label={`${label}をドラッグで並び替え`} disabled={disabled} sx={{ width: 24, flexShrink: 0, p: 0, cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', color: muted }}><DragIndicatorRoundedIcon sx={{ fontSize: 20 }} /></IconButton></span>
      </Tooltip>
      <Button onClick={onCourse} sx={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: COURSE_COLS, gap: 1.5, alignItems: 'center', textAlign: 'left', px: 0, py: 1, color: 'text.primary', borderRadius: 0 }}>
        <Box component="span" sx={{ fontSize: 13, color: muted, textAlign: 'center' }}>{index + 1}</Box>
        <Box component="span" sx={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</Box>
        <Box component="span" sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: course.name ? 'text.primary' : muted }}>{course.name || '未設定'}</Box>
          <Box sx={{ fontSize: 12, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{names.length ? names.join(' → ') : '配送先なし'}</Box>
        </Box>
        <Box component="span" sx={{ fontSize: 13, color: muted, whiteSpace: 'nowrap' }}>{names.length}件</Box>
      </Button>
      <Tooltip title="ダイヤから外す（コースは残ります）"><span><IconButton aria-label={`${label}をダイヤから外す`} disabled={disabled} onClick={onRemove} sx={{ width: 40, flexShrink: 0 }}><CloseRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
    </Box>
  );
}
